import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  useGetLocationDetailsQuery,
  useGetPlaceDetailsQuery,
  useGetAllUsersQuery,
  useUploadLocationPhotoMutation,
  useUpdateLocationMutation,
  useGetAllPostsQuery,
  useGetPlacesQuery,
  useGetLocationsQuery
} from '../../redux/adminApiSlice';
import { cn } from '../../spreadsheet/src/utils/cn';
import { convertToProxyUrl } from '../../utils/photoProxy';
import DisplayJSON from '../DisplayJSON/DisplayJSON';
import PostDetails from '../PostDetails/PostDetails';

export interface LocationData {
  place?: any;
  neighborhood?: any;
  city?: any;
  state?: any;
  country?: any;
}

export interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationData: LocationData;
  currentPostLevel?: string; // The level where the post currently exists
  onLocationClick?: (location: any, locationLevel: string) => void; // Callback when a location is clicked
}

const LocationModal: React.FC<LocationModalProps> = ({
  isOpen,
  onClose,
  locationData,
  currentPostLevel,
  onLocationClick
}) => {
  const [selectedLocationLevel, setSelectedLocationLevel] = useState<string | null>(null);
  const [editingLocationName, setEditingLocationName] = useState<string>('');
  const [isEditingLocationName, setIsEditingLocationName] = useState(false);
  const [locationModalTab, setLocationModalTab] = useState<'users' | 'posts' | 'places'>('users');
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const previousLocationDataRef = useRef<LocationData | null>(null);

  // Initialize selected level to the current post level or first available level
  useEffect(() => {
    if (isOpen && !selectedLocationLevel) {
      if (currentPostLevel) {
        const level = currentPostLevel.toLowerCase();
        if (locationData[level as keyof LocationData]) {
          setSelectedLocationLevel(level);
          return;
        }
      }
      // Fallback to first available level
      const levels = ['place', 'neighborhood', 'city', 'state', 'country'] as const;
      for (const level of levels) {
        if (locationData[level]) {
          setSelectedLocationLevel(level);
          break;
        }
      }
    }
  }, [isOpen, currentPostLevel, locationData, selectedLocationLevel]);

  // Update selectedLocationLevel when locationData changes (e.g., when a location is clicked)
  // Select the most specific (lowest) level that exists in locationData
  // This ensures the hierarchy button matches the clicked location
  // Only runs when locationData actually changes, not when selectedLocationLevel changes
  useEffect(() => {
    if (isOpen && locationData) {
      // Check if locationData actually changed by comparing location IDs
      // Handle both _id and id fields
      const getLocationId = (loc: any) => loc?._id || loc?.id || null;
      const currentLocationId = getLocationId(locationData.place) || 
                                getLocationId(locationData.neighborhood) || 
                                getLocationId(locationData.city) || 
                                getLocationId(locationData.state) || 
                                getLocationId(locationData.country);
      const previousLocationId = getLocationId(previousLocationDataRef.current?.place) || 
                                 getLocationId(previousLocationDataRef.current?.neighborhood) || 
                                 getLocationId(previousLocationDataRef.current?.city) || 
                                 getLocationId(previousLocationDataRef.current?.state) || 
                                 getLocationId(previousLocationDataRef.current?.country);
      
      if (currentLocationId !== previousLocationId) {
        // Prioritize currentPostLevel if it exists in locationData
        let targetLevel: string | null = null;
        if (currentPostLevel) {
          const postLevel = currentPostLevel.toLowerCase();
          if (locationData[postLevel as keyof LocationData]) {
            targetLevel = postLevel;
          }
        }
        
        // If no target level from currentPostLevel, find the most specific (lowest) level that exists
        if (!targetLevel) {
          const levels = ['place', 'neighborhood', 'city', 'state', 'country'] as const;
          for (const level of levels) {
            if (locationData[level]) {
              targetLevel = level;
              break; // Break on first (most specific) level found
            }
          }
        }
        
        // Only update if we found a level
        if (targetLevel) {
          setSelectedLocationLevel(targetLevel);
        }
        // Update the ref to track the current locationData
        previousLocationDataRef.current = locationData;
      }
    }
  }, [isOpen, locationData, currentPostLevel]);

  // Get location details when a level is selected
  const currentLocationId = useMemo(() => {
    if (!selectedLocationLevel) return null;
    const location = locationData[selectedLocationLevel as keyof LocationData];
    
    if (location && typeof location === 'object') {
      // Try _id first, then id
      if ('_id' in location && location._id) {
        return location._id;
      }
      if ('id' in location && (location as any).id) {
        return (location as any).id;
      }
    }
    return null;
  }, [selectedLocationLevel, locationData]);

  // Get location details when a location level is selected (not place)
  const { data: locationDetails, refetch: refetchLocationDetails } = useGetLocationDetailsQuery(
    currentLocationId || '',
    { skip: !currentLocationId || selectedLocationLevel === 'place' }
  );

  // Get place details when place level is selected
  const { data: placeDetails } = useGetPlaceDetailsQuery(
    currentLocationId || '',
    { skip: !currentLocationId || selectedLocationLevel !== 'place' }
  );

  // Get posts for the selected location - load when modal opens
  const { data: locationPostsData } = useGetAllPostsQuery({
    locationId: currentLocationId || undefined,
    limit: 100,
  }, {
    skip: !currentLocationId
  });

  // Get all places and filter by location - load when modal opens
  const { data: allPlaces = [] } = useGetPlacesQuery(undefined, {
    skip: !currentLocationId
  });

  // Get all locations to show child locations (neighborhoods, cities, states)
  const { data: allLocations = [] } = useGetLocationsQuery(undefined, {
    skip: !currentLocationId
  });

  // Get all users for filtering by location
  const { data: allUsers = [] } = useGetAllUsersQuery();

  const [uploadLocationPhoto] = useUploadLocationPhotoMutation();
  const [updateLocation] = useUpdateLocationMutation();

  // Helper function to get profile picture URL
  const getProfilePicture = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('http')) {
      return url;
    }
    if (url.startsWith('file://')) {
      return undefined;
    }
    const apiBaseUrl = localStorage.getItem('useRemoteApi') === 'true'
      ? process.env.REACT_APP_REMOTE_API_URL || 'https://api.gridappsocial.com'
      : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3000';
    return `${apiBaseUrl}/${url}`;
  };

  // Handle saving location name
  const handleSaveLocationName = async () => {
    if (!currentLocationId || !editingLocationName.trim()) return;
    
    try {
      await updateLocation({
        id: currentLocationId,
        updates: { name: editingLocationName.trim() }
      }).unwrap();
      setIsEditingLocationName(false);
      setEditingLocationName('');
      refetchLocationDetails();
    } catch (error) {
      console.error('Error updating location name:', error);
      alert('Failed to update location name');
    }
  };

  // Calculate places/locations data based on selected level - memoized to update when level changes
  // Must be before early return to follow rules of hooks
  const placesTabData = useMemo(() => {
    if (!currentLocationId || !selectedLocationLevel) {
      return {
        childLocations: [],
        placesInLocation: [],
        displayType: 'places' as 'locations' | 'places',
        childLevel: null as string | null,
        count: 0
      };
    }

    let childLocations: any[] = [];
    let placesInLocation: any[] = [];
    let displayType: 'locations' | 'places' = 'places';
    let childLevel: string | null = null;

    // Get child locations based on selected level
    if (selectedLocationLevel === 'city') {
      childLevel = 'Neighborhood';
      childLocations = allLocations.filter((loc: any) => {
        const locParentId = typeof loc.parent === 'object' && loc.parent?._id
          ? loc.parent._id.toString()
          : (typeof loc.parent === 'string' ? loc.parent : null);
        return loc.level === 'Neighborhood' && locParentId === currentLocationId.toString();
      });
      displayType = childLocations.length > 0 ? 'locations' : 'places';
    } else if (selectedLocationLevel === 'state') {
      childLevel = 'City';
      childLocations = allLocations.filter((loc: any) => {
        const locParentId = typeof loc.parent === 'object' && loc.parent?._id
          ? loc.parent._id.toString()
          : (typeof loc.parent === 'string' ? loc.parent : null);
        return loc.level === 'City' && locParentId === currentLocationId.toString();
      });
      displayType = childLocations.length > 0 ? 'locations' : 'places';
    } else if (selectedLocationLevel === 'country') {
      childLevel = 'State';
      childLocations = allLocations.filter((loc: any) => {
        const locParentId = typeof loc.parent === 'object' && loc.parent?._id
          ? loc.parent._id.toString()
          : (typeof loc.parent === 'string' ? loc.parent : null);
        return loc.level === 'State' && locParentId === currentLocationId.toString();
      });
      displayType = childLocations.length > 0 ? 'locations' : 'places';
    }

    // If no child locations found, or if at neighborhood/place level, show places
    if (displayType === 'places' || selectedLocationLevel === 'neighborhood' || selectedLocationLevel === 'place') {
      placesInLocation = allPlaces.filter((place: any) => {
        let placeLocationId: string | null = null;

        if (selectedLocationLevel === 'neighborhood' && place.neighborhood) {
          placeLocationId = typeof place.neighborhood === 'object' && place.neighborhood?._id
            ? place.neighborhood._id.toString()
            : (typeof place.neighborhood === 'string' ? place.neighborhood : null);
        } else if (selectedLocationLevel === 'city' && place.city) {
          placeLocationId = typeof place.city === 'object' && place.city?._id
            ? place.city._id.toString()
            : (typeof place.city === 'string' ? place.city : null);
        } else if (selectedLocationLevel === 'state' && place.state) {
          placeLocationId = typeof place.state === 'object' && place.state?._id
            ? place.state._id.toString()
            : (typeof place.state === 'string' ? place.state : null);
        } else if (selectedLocationLevel === 'country' && place.country) {
          placeLocationId = typeof place.country === 'object' && place.country?._id
            ? place.country._id.toString()
            : (typeof place.country === 'string' ? place.country : null);
        }

        return placeLocationId && currentLocationId && placeLocationId.toString() === currentLocationId.toString();
      });
    }

    const count = displayType === 'locations' && childLocations.length > 0
      ? childLocations.length
      : placesInLocation.length;

    return {
      childLocations,
      placesInLocation,
      displayType,
      childLevel,
      count
    };
  }, [currentLocationId, selectedLocationLevel, allLocations, allPlaces]);

  // Get users in this location - memoized to update when level changes
  const usersInLocation = useMemo(() => {
    if (!currentLocationId) return [];
    return allUsers.filter(user => {
      const homeNeighborhood = user.homeNeighborhood;
      const currentNeighborhood = user.currentNeighborhood;
      
      if (typeof homeNeighborhood === 'object' && homeNeighborhood?._id === currentLocationId) return true;
      if (typeof homeNeighborhood === 'string' && homeNeighborhood === currentLocationId) return true;
      if (typeof currentNeighborhood === 'object' && currentNeighborhood?._id === currentLocationId) return true;
      if (typeof currentNeighborhood === 'string' && currentNeighborhood === currentLocationId) return true;
      
      return false;
    });
  }, [currentLocationId, allUsers]);

  // Filter posts to exclude mock, soft-deleted, and comments - memoized
  const filteredPosts = useMemo(() => {
    if (!locationPostsData?.data) return [];
    return locationPostsData.data.filter((post: any) => {
      // Filter out mock posts
      if (post.isMockPost === true) return false;
      // Also check if user is a mock user (if user is populated)
      if (post.user && typeof post.user === 'object' && post.user.isMockUser === true) return false;
      // Filter out soft-deleted posts
      if (post.isDeleted === true) return false;
      // Filter out comments (comments have a parentPost field)
      if (post.parentPost) return false;
      return true;
    });
  }, [locationPostsData?.data]);

  if (!isOpen || !selectedLocationLevel) return null;

  const location = locationData[selectedLocationLevel as keyof LocationData];
  const locationDetailsData = locationDetails || location;

  if (!location || typeof location !== 'object' || !('name' in location)) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-8 text-center text-gray-500">
            No {selectedLocationLevel} data available
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For places, use placeDetails; for locations, use locationDetails
  // Convert to proxy URL if it's a Google photo URL
  const rawPhotoUrl = selectedLocationLevel === 'place' 
    ? (placeDetails?.photo || location?.photo || null)
    : (locationDetailsData?.photo || location?.photo || null);
  const photoUrl = convertToProxyUrl(rawPhotoUrl);
  const locationName = locationDetailsData?.name || location?.name || 'Unknown';
  const displayLocationId = locationDetailsData?._id || location?._id || 'N/A';
  const currentPostLevelLower = currentPostLevel?.toLowerCase() || '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Location Details</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowJsonDialog(true)}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors flex items-center gap-1.5"
              title="View JSON"
            >
              <span>📄</span>
              <span>View JSON</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Hierarchy Strip */}
        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 flex-wrap">
            {(['place', 'neighborhood', 'city', 'state', 'country'] as const).map((level, index) => {
              const loc = locationData[level];
              const icons: Record<string, string> = {
                place: '📍',
                neighborhood: '🏘️',
                city: '🌆',
                state: '🗺️',
                country: '🌎'
              };
              const icon = icons[level] || '📍';
              const isActive = selectedLocationLevel === level;
              const isCurrentPostLevel = currentPostLevelLower === level;
              const hasLocation = loc && typeof loc === 'object' && 'name' in loc;

              if (!hasLocation) {
                return (
                  <React.Fragment key={level}>
                    <span className="text-gray-400 text-sm">{icon} {level.charAt(0).toUpperCase() + level.slice(1)}</span>
                    {index < 4 && <span className="text-gray-300">→</span>}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={level}>
                  <button
                    onClick={() => setSelectedLocationLevel(level)}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5',
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    )}
                  >
                    <span>{icon}</span>
                    <span>{loc.name}</span>
                    {isCurrentPostLevel && (
                      <span className={cn(
                        'w-2 h-2 rounded-full',
                        isActive ? 'bg-white' : 'bg-blue-600'
                      )} title="Current post level" />
                    )}
                  </button>
                  {index < 4 && <span className="text-gray-400">→</span>}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column - Photo and Basic Info */}
            <div className="md:col-span-1 space-y-4">
              {/* Photo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location Photo</label>
                <div className="relative group">
                  <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 relative">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={locationName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    {!photoUrl && (
                      <div className="absolute inset-0 w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-4xl">📷</span>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="location-photo-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file && currentLocationId) {
                                try {
                                  await uploadLocationPhoto({ photo: file, locationId: currentLocationId }).unwrap();
                          refetchLocationDetails();
                        } catch (error) {
                          console.error('Error uploading photo:', error);
                          alert('Failed to upload photo');
                        }
                      }
                    }}
                  />
                  <label
                    htmlFor="location-photo-upload"
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity cursor-pointer rounded-lg"
                  >
                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                      Click to change photo
                    </span>
                  </label>
                </div>
              </div>

              {/* Location Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location Name</label>
                {isEditingLocationName ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editingLocationName}
                      onChange={(e) => setEditingLocationName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveLocationName();
                        } else if (e.key === 'Escape') {
                          setIsEditingLocationName(false);
                          setEditingLocationName('');
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveLocationName}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingLocationName(false);
                        setEditingLocationName('');
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div
                    className="px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                    onClick={() => {
                      setEditingLocationName(locationName);
                      setIsEditingLocationName(true);
                    }}
                  >
                    <span className="text-gray-900">{locationName}</span>
                    <span className="text-gray-400 text-sm">✏️</span>
                  </div>
                )}
              </div>

              {/* Location ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location ID</label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-gray-700 truncate">{displayLocationId}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(displayLocationId);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                      title="Copy ID"
                    >
                      📋
                    </button>
                  </div>
                </div>
              </div>

              {/* Online Users and Posts Count */}
              <div className="space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Online Users</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-gray-900">
                      {(() => {
                        // For places, onlineUsers might be an array
                        if (selectedLocationLevel === 'place') {
                          const placeOnlineUsers = placeDetails?.onlineUsers;
                          if (Array.isArray(placeOnlineUsers)) {
                            return placeOnlineUsers.length;
                          }
                          return typeof placeOnlineUsers === 'number' ? placeOnlineUsers : 0;
                        }
                        // For locations, check locationDetails first (from query), then location object, then metrics
                        const detailsOnlineUsers = locationDetailsData?.onlineUsers;
                        const locationOnlineUsers = location?.onlineUsers;
                        const metricsOnlineUsers = locationDetailsData?.metrics?.onlineUsers;
                        
                        // Return the first available value
                        if (typeof detailsOnlineUsers === 'number') return detailsOnlineUsers;
                        if (typeof locationOnlineUsers === 'number') return locationOnlineUsers;
                        if (typeof metricsOnlineUsers === 'number') return metricsOnlineUsers;
                        return 0;
                      })()}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Non-Expired Posts</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-gray-900">
                      {locationPostsData?.data 
                        ? locationPostsData.data.filter((post: any) => {
                            if (!post.expiresAt) return true; // No expiry = never expires
                            const expiryDate = new Date(post.expiresAt);
                            const now = new Date();
                            return expiryDate > now;
                          }).length
                        : 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Tabs */}
            <div className="md:col-span-2">
              {/* Tabs */}
              <div className="mb-4 border-b border-gray-200">
                <div className="flex gap-1">
                  <button
                    onClick={() => setLocationModalTab('users')}
                    className={cn(
                      'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                      locationModalTab === 'users'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    Users ({usersInLocation.length})
                  </button>
                  <button
                    onClick={() => setLocationModalTab('posts')}
                    className={cn(
                      'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                      locationModalTab === 'posts'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    Posts ({filteredPosts.length})
                  </button>
                  <button
                    onClick={() => setLocationModalTab('places')}
                    className={cn(
                      'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                      locationModalTab === 'places'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    {placesTabData.displayType === 'locations' && placesTabData.childLevel
                      ? `${placesTabData.childLevel}s (${placesTabData.count})`
                      : `Places (${placesTabData.count})`}
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="max-h-[500px] overflow-y-auto">
                {locationModalTab === 'users' && (
                  <>
                    {usersInLocation.length === 0 ? (
                      <p className="text-gray-500 text-sm">No users found in this location</p>
                    ) : (
                      <div className="space-y-2">
                        {usersInLocation.map((user) => {
                          const profilePictureUrl = getProfilePicture(user.facePicture || user.profilePicture);
                          return (
                            <div
                              key={user._id}
                              className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-3"
                            >
                              {profilePictureUrl ? (
                                <img
                                  src={profilePictureUrl}
                                  alt={user.displayName || user.username || 'User'}
                                  className="w-10 h-10 rounded-full object-cover shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-gray-600 text-sm font-medium shrink-0 ${
                                  profilePictureUrl ? 'hidden' : 'bg-gray-200'
                                }`}
                              >
                                {user.displayName?.[0]?.toUpperCase() || user.username?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">
                                  {user.displayName || user.username || 'Unknown User'}
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                  @{user.username || 'no-username'}
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 font-mono">
                                {user._id.substring(0, 8)}...
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {locationModalTab === 'posts' && (
                  <>
                    {filteredPosts.length === 0 ? (
                      <p className="text-gray-500 text-sm">No posts found in this location</p>
                    ) : (
                      <div className="space-y-2">
                        {filteredPosts.map((post) => {
                          const createdAt = new Date(post.createdAt);
                          const dayOfWeek = createdAt.toLocaleDateString('en-US', { weekday: 'long' });
                          const dateTime = createdAt.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          });
                          
                          // Calculate expiry status
                          let expiryStatus = '';
                          if (post.expiresAt) {
                            const expiryDate = new Date(post.expiresAt);
                            const now = new Date();
                            if (expiryDate <= now) {
                              expiryStatus = 'Expired';
                            } else {
                              const diff = expiryDate.getTime() - now.getTime();
                              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                              const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                              const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                              
                              if (days > 0) {
                                expiryStatus = `${days}d ${hours}h left`;
                              } else if (hours > 0) {
                                expiryStatus = `${hours}h ${minutes}m left`;
                              } else {
                                expiryStatus = `${minutes}m left`;
                              }
                            }
                          } else {
                            expiryStatus = 'Never expires';
                          }
                          
                          const isExpired = post.expiresAt && new Date(post.expiresAt) <= new Date();
                          
                          return (
                            <div
                              key={post._id}
                              onClick={() => setSelectedPost(post)}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                isExpired 
                                  ? 'border-red-200 bg-red-50 hover:bg-red-100' 
                                  : 'border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate mb-1">
                                    {post.text || '(No text)'}
                                  </div>
                                  <div className="text-xs text-gray-600 space-y-0.5">
                                    <div>
                                      <span className="font-medium">{dayOfWeek}</span>, {dateTime}
                                    </div>
                                    <div className={`flex items-center gap-1.5 ${
                                      isExpired ? 'text-red-600' : 'text-gray-500'
                                    }`}>
                                      <span className={isExpired ? 'font-medium' : ''}>
                                        {expiryStatus}
                                      </span>
                                      {post.media && (post.media.photo || post.media.video) && (
                                        <span className="text-gray-400" title="Has media">
                                          📷
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-400 font-mono shrink-0">
                                  {post._id.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {locationModalTab === 'places' && (
                  <>
                    {placesTabData.displayType === 'locations' && placesTabData.childLocations.length > 0 ? (
                      <div className="space-y-2">
                        {placesTabData.childLocations.map((loc: any) => (
                          <div
                            key={loc._id}
                            onClick={() => {
                              if (onLocationClick) {
                                const level = (loc.level || placesTabData.childLevel || '').toLowerCase();
                                onLocationClick(loc, level);
                              }
                            }}
                            className={cn(
                              "p-3 border border-gray-200 rounded-lg transition-colors",
                              onLocationClick ? "cursor-pointer hover:bg-blue-50 hover:border-blue-300" : "hover:bg-gray-50"
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate mb-1">
                                  {loc.name || 'Unnamed Location'}
                                </div>
                                <div className="text-xs text-gray-500 mb-1">
                                  Level: {loc.level || placesTabData.childLevel}
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 font-mono shrink-0">
                                {loc._id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : placesTabData.placesInLocation.length > 0 ? (
                      <div className="space-y-2">
                        {placesTabData.placesInLocation.map((place: any) => (
                          <div
                            key={place._id}
                            className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate mb-1">
                                  {place.name || 'Unnamed Place'}
                                </div>
                                {place.type && (
                                  <div className="text-xs text-gray-500 mb-1">
                                    Type: {place.type}
                                  </div>
                                )}
                                {place.address && (
                                  <div className="text-xs text-gray-500">
                                    {place.address}
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-gray-400 font-mono shrink-0">
                                {place._id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No places found in this location</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* JSON Dialog */}
      {showJsonDialog && (
        <DisplayJSON
          open={showJsonDialog}
          onClose={() => setShowJsonDialog(false)}
          data={selectedLocationLevel === 'place' ? placeDetails || location : locationDetailsData || location}
          title="Location Object"
        />
      )}

      {/* Post Details Dialog */}
      <PostDetails
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
      />
    </div>
  );
};

export default LocationModal;
